import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Laptop, ArrowRight, Hourglass, CheckCircle2, XCircle, Infinity as InfinityIcon } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';

/**
 * Compact "My company assets" widget for the dashboard.
 * - Hides itself when the user has zero assets to keep the dashboard clean.
 * - Surfaces pending-acknowledgement assets prominently and lets the user
 *   acknowledge / mark not-received without leaving the dashboard.
 */
export default function MyAssetsWidget() {
  const { user } = useAuth();
  const [rows, setRows] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get(`/assets/by-employee/${user.id}`);
      setRows((data || []).filter(a => a.status === 'assigned'));
    } catch (e) { setRows([]); }
  };

  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/assets/by-employee/${user.id}`);
        if (!cancelled) setRows((data || []).filter(a => a.status === 'assigned'));
      } catch (e) { if (!cancelled) setRows([]); }
    })();
    return () => { cancelled = true; };
  }, [user?.id]);

  const acknowledge = async (aid) => {
    try {
      await api.post(`/assets/${aid}/acknowledge`);
      toast.success('Receipt acknowledged');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not acknowledge'); }
  };

  const reject = async (aid) => {
    const reason = window.prompt('Briefly describe why this asset was NOT received (optional):') || '';
    try {
      await api.post(`/assets/${aid}/reject-receipt`, { reason });
      toast.success('Reported — IT admin notified');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not submit'); }
  };

  if (rows === null) return null;       // initial load — render nothing
  if (rows.length === 0) return null;   // no assets — keep dashboard uncluttered

  const pending = rows.filter(a => a.current_assignment?.acknowledgement_status === 'pending');

  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm" data-testid="my-assets-widget">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">My company assets</div>
          <h2 className="font-heading text-xl font-semibold text-slate-900 mt-1">
            {rows.length} {rows.length === 1 ? 'item' : 'items'} assigned to you
          </h2>
          {pending.length > 0 && (
            <div className="text-xs text-amber-700 mt-1">{pending.length} await your acknowledgement.</div>
          )}
        </div>
        <Link to="/profile" className="text-xs font-semibold text-[#3D4A8E] hover:underline inline-flex items-center gap-1" data-testid="my-assets-view-all">
          View all <ArrowRight className="w-3 h-3" />
        </Link>
      </div>

      <div className="mt-4 space-y-2">
        {rows.slice(0, 3).map(a => {
          const ca = a.current_assignment || {};
          const isPending = ca.acknowledgement_status === 'pending';
          return (
            <div key={a.id} className="border border-slate-200 rounded-xl p-3" data-testid={`my-asset-${a.id}`}>
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-slate-50 border border-slate-200 flex items-center justify-center text-slate-500 shrink-0">
                  <Laptop className="w-4 h-4" strokeWidth={1.8} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-slate-900 text-sm truncate">{a.name}</div>
                  <div className="text-[11px] text-slate-500 truncate">
                    <span className="font-mono">{a.asset_id}</span> · {a.category}
                    {ca.until_offboarding && <> · <InfinityIcon className="w-3 h-3 inline" /> until offboarding</>}
                    {!ca.until_offboarding && ca.expected_return_date && <> · return by {ca.expected_return_date}</>}
                  </div>
                </div>
                {isPending ? (
                  <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full border bg-amber-50 text-amber-700 border-amber-200 inline-flex items-center gap-1">
                    <Hourglass className="w-3 h-3" /> pending
                  </span>
                ) : (
                  <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full border bg-emerald-50 text-emerald-700 border-emerald-200">
                    acknowledged
                  </span>
                )}
              </div>
              {isPending && (
                <div className="flex gap-2 mt-2 pl-12">
                  <Button size="sm" className="rounded-full h-7 text-[11px] bg-emerald-600 hover:bg-emerald-700" onClick={() => acknowledge(a.id)} data-testid={`dash-ack-${a.id}`}>
                    <CheckCircle2 className="w-3 h-3 mr-1" /> Acknowledge
                  </Button>
                  <Button size="sm" variant="outline" className="rounded-full h-7 text-[11px] border-rose-300 text-rose-700 hover:bg-rose-50" onClick={() => reject(a.id)} data-testid={`dash-reject-${a.id}`}>
                    <XCircle className="w-3 h-3 mr-1" /> Not received
                  </Button>
                </div>
              )}
            </div>
          );
        })}
        {rows.length > 3 && (
          <div className="text-xs text-slate-500 text-center pt-1">
            + {rows.length - 3} more — see Profile
          </div>
        )}
      </div>
    </div>
  );
}
